<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { CriteriaInstance } from "types/Checklist";
import { EvaluationRelevantInstanceType } from "~/types/enums/EvaluationRelevantInstanceType";
import {
  EvaluationCriteriaConfig,
  EvaluationRelevantInstance,
} from "~/types/EvaluationResults";

// @ts-ignore
import { TransitionExpand } from "@morev/vue-transitions";

const props = defineProps({
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  criteriaConfig: {
    type: Object as PropType<EvaluationCriteriaConfig>,
    required: true,
  },
  relevantInstances: {
    type: Object as PropType<EvaluationRelevantInstance[]>,
    required: true,
  },
  paragraphPosition: {
    type: Number,
    required: false,
  },
  showAll: {
    type: Boolean,
    required: true,
  },
});

const emits = defineEmits(["reportProblem"]);
const documentId = inject("documentId") as any;
const store = useResultsForCriteriaIdStore(documentId, props.criteriaInstance);
const { criteriaParagraphPositionsData } = storeToRefs(store);

const documentChecklistStore = useDocumentChecklistStore(documentId);
const { getCriteriaFromId } = documentChecklistStore;

const criteria = computed(() => {
  return props.criteriaInstance.checklistCriteriaId
    ? getCriteriaFromId(props.criteriaInstance.checklistCriteriaId)
    : undefined;
});

// Is thisa paragraph criteria instance where we need to show points
// And if it is, we and hte number of relevantInstances is less than the checklist paragraph count
// Then we need to change how we're doing things.

const isParagraphPointsCriteria = computed(() => {
  return props.criteriaConfig.acceptParagraphPoints == true;
});

// If we're not using paragraph points then we have the correct number of instances
// If we're not, then we should check if the checklist paragraph count is equal to the relvant instances
const hasCorrectNumberOfParagraphs = computed(() => {
  return (
    isParagraphPointsCriteria.value != true ||
    numberOfChecklistParagraphs.value == props.relevantInstances.length
  );
});

const numberOfChecklistParagraphs = computed(() => {
  return criteriaParagraphPositionsData.value.length ?? 0;
});

const missingInstances = computed((): any[] => {
  if (hasCorrectNumberOfParagraphs.value == true) {
    return [];
  }

  var numMissingInstances =
    numberOfChecklistParagraphs.value - props.relevantInstances.length;

  // If the number of instances is greater than the checklist paragraphs then we're not going to do anything else
  if (numMissingInstances <= 0) {
    return [];
  }

  // If num missing instances is a positive number it means we're expecting
  // more paragraphs than what the student submitted
  // So we need to create some dummy instances
  var index = props.relevantInstances.length;

  var missingValues = [];
  while (index < numberOfChecklistParagraphs.value) {
    missingValues.push({
      uid: `missing-${index}`,
      valid: false,
      type: EvaluationRelevantInstanceType.paragraph,
      position: index,
      paragraphPosition: undefined,
      criteria: [],
      tokens: [],
      text: "",
    });

    index++;
  }

  return missingValues;
});

const onReportProblem = (
  relevantInstance: EvaluationRelevantInstance,
  isControl: boolean
) => {
  emits("reportProblem", relevantInstance, isControl);
};

const documentTokenIdPositionMap =
  useDocumentTokenIdPositionMapStore(documentId);
const { tokenGlobalPositionFromId } = storeToRefs(documentTokenIdPositionMap);

const highlightAll = (color: number[]) => {
  const matchingInstances = props.relevantInstances.map((instance) =>
    instance.tokens.map((token) => {
      return tokenGlobalPositionFromId.value(token.id);
    })
  );

  // Flatten the matching Instances
  const tokenGlobalPositions = matchingInstances.flat();

  if (tokenGlobalPositions.length == 0) return;

  useDocumentSelectionStore(documentId).setSelection(undefined, undefined);
  useDocumentSelectionStore(documentId).setCustomSelection(
    tokenGlobalPositions
  );

  useTokenFormatter().formatSelectedTokens(documentId, {
    highlightColor: color,
  });

  useDocumentSelectionStore(documentId).clearSelection();
};

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);
</script>

<template>
  <div>
    <div
      v-if="isTeacherViewMode && criteria?.allowHighlightAll == true"
      class="w-full flex flex-row items-center"
    >
      <ToolbarTextHighlightButton
        :height="30"
        :size="30"
        class="flex flex-col justify-center"
        data-step-id="toolbar-highlight-button"
        @select-color="($event) => highlightAll($event)"
      />
      <span> Highlight all </span>
    </div>

    <TransitionExpand group>
      <EvaluationResultsInstancesListItem
        v-for="(relevantInstance, index) in relevantInstances"
        :relevant-instance="relevantInstance"
        :criteria-instance="criteriaInstance"
        :criteria-config="criteriaConfig"
        :index="paragraphPosition ?? index"
        :key="`${relevantInstance.uid}-${index}`"
        :paragraph-position="paragraphPosition"
        :has-multiple-instances="
          showAll &&
          relevantInstances.length > 1 &&
          paragraphPosition != undefined
        "
        @reportProblem="onReportProblem"
        class="item"
      />
    </TransitionExpand>
    <EvaluationResultsInstancesListItem
      v-for="(relevantInstance, index) in missingInstances"
      :relevant-instance="relevantInstance"
      :criteria-instance="criteriaInstance"
      :criteria-config="criteriaConfig"
      :index="paragraphPosition ?? relevantInstance.position"
      :key="`${relevantInstance.uid}-${index}`"
      :paragraph-position="paragraphPosition"
      :is-placeholder="true"
      :has-multiple-instances="showAll && relevantInstances.length > 1"
      @reportProblem="onReportProblem"
    />
  </div>
</template>

<style scoped>
.container {
  position: relative;
  padding: 0;
}

.fade-move,
.fade-enter-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
</style>
